import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Upload, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { Button } from '../ui/Button';
import { useAuthStore } from '../../store/useAuthStore';
import { useDossier } from '../../hooks/queries/useDossiers';
import { HearingFields, useHearingPeopleOptions } from '../dossiers/HearingFields';
import {
  applyCourtPrefill,
  commitHearingDraft,
  emptyHearingDraft,
  type HearingDraft,
} from '../../services/dossiers/hearingForm';
import { useUploadFile } from '../../hooks/queries/useDossierFiles';
import { MAX_FILE_SIZE, formatFileSize } from '../../services/files/fileUtils';

import { TaskComposer } from '../tasks/TaskComposer';
import { TaskCreateModal } from '../tasks/TaskCreateModal';
import { useTaskQuickCapture } from '../../hooks/queries/useCabinetSettings';
import { TimeEntryModal } from '../timeTracking/TimeEntryModal';
import { DossierPickStep } from './DossierPickStep';
import { useCreateDossierHearing } from '../../hooks/queries/useDossierHearings';

function Shell({
  title,
  onClose,
  children,
  wide,
  bare,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
  bare?: boolean;
}) {
  const width = bare ? 'max-w-[920px] bg-[#fffdf8] rounded-2xl' : wide ? 'max-w-xl bg-white rounded-cime' : 'max-w-md bg-white rounded-cime';
  const frame = bare ? `${width} shadow-[0_24px_60px_rgba(2,20,16,0.28)]` : `shadow-xl ${width}`;
  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`relative w-full overflow-hidden flex flex-col max-h-[90vh] ${frame}`}
        onClick={(e) => e.stopPropagation()}
      >
        {bare ? (
          <button
            type="button"
            onClick={onClose}
            className="absolute right-4 top-4 z-10 text-neutral-400 hover:text-neutral-700"
            aria-label="Fermer"
          >
            <X className="w-4 h-4" />
          </button>
        ) : (
          <header className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 bg-white">
            <h2 className="font-heading text-lg font-semibold text-cime-text-primary">{title}</h2>
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-400 hover:text-neutral-700"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </header>
        )}
        {children}
      </div>
    </div>
  );
}

export function QuickCreateFileDialog({
  contextDossierId,
  onClose,
}: {
  contextDossierId: string | null;
  onClose: () => void;
}) {
  const [dossierId, setDossierId] = useState<string | null>(contextDossierId);
  const collaboratorId = useAuthStore((s) => s.collaborator?.id);
  const upload = useUploadFile(dossierId ?? '');
  const [busy, setBusy] = useState(false);

  const onDrop = useCallback(
    async (accepted: File[]) => {
      if (!dossierId || !collaboratorId) return;
      setBusy(true);
      try {
        for (const file of accepted) {
          if (file.size > MAX_FILE_SIZE) {
            toast.error(`${file.name} : trop volumineux (max ${formatFileSize(MAX_FILE_SIZE)})`);
            continue;
          }
          await upload.mutateAsync({ file, uploaderId: collaboratorId, folderId: null });
        }
        toast.success('Fichier enregistré');
        onClose();
      } catch (e) {
        toast.error(e instanceof Error ? e.message : 'Échec de l’envoi');
      } finally {
        setBusy(false);
      }
    },
    [collaboratorId, dossierId, onClose, upload],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    disabled: !dossierId || busy,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <Shell title="Nouveau fichier" onClose={onClose}>
      <DossierPickStep selectedId={dossierId} onSelect={setDossierId} />
      <div className="p-5" {...getRootProps()}>
        <input {...getInputProps()} />
        {!dossierId ? (
          <p className="text-sm text-neutral-400 text-center py-4">
            Choisissez un dossier pour déposer un fichier.
          </p>
        ) : (
          <div
            className={`rounded-cime border-2 border-dashed px-4 py-8 text-center ${
              isDragActive ? 'border-cime-primary bg-cime-primary/5' : 'border-neutral-200'
            }`}
          >
            <p className="text-sm text-neutral-500 mb-3">
              {isDragActive ? 'Déposez les fichiers ici' : 'Glissez-déposez, ou'}
            </p>
            <Button type="button" onClick={open} disabled={busy}>
              <Upload className="w-4 h-4 mr-1.5" />
              Parcourir
            </Button>
          </div>
        )}
      </div>
    </Shell>
  );
}

export function QuickCreateAudienceDialog({
  contextDossierId,
  onClose,
}: {
  contextDossierId: string | null;
  onClose: () => void;
}) {
  const [dossierId, setDossierId] = useState<string | null>(contextDossierId);
  const [repick, setRepick] = useState(false);
  const create = useCreateDossierHearing(dossierId ?? '');
  const meId = useAuthStore((s) => s.collaborator?.id ?? null);
  const dossier = useDossier(dossierId ?? undefined);
  const [draft, setDraft] = useState<HearingDraft>(() => emptyHearingDraft(meId));

  useEffect(() => {
    if (!meId) return;
    setDraft((current) => (current.ownerId ? current : emptyHearingDraft(meId)));
  }, [meId]);

  useEffect(() => {
    setDraft((current) => applyCourtPrefill(current, dossier.data?.juridiction ?? null, 'create'));
  }, [dossier.data?.id, dossier.data?.juridiction]);

  const keepIds = useMemo(() => {
    const ids = draft.people
      .filter((person) => person.kind === 'collaborator')
      .map((person) => person.id);
    if (draft.ownerId) ids.push(draft.ownerId);
    return ids;
  }, [draft.ownerId, draft.people]);
  const options = useHearingPeopleOptions(dossierId ?? undefined, keepIds);
  const committed = commitHearingDraft(draft, {
    collaboratorIds: options.collaborators.map((person) => person.id),
    contactIds: options.parties.map((person) => person.id),
  });

  function save() {
    if (!dossierId || !committed.ok) return;
    create.mutate(
      { ...committed.payload, people: committed.people },
      {
        onSuccess: () => {
          toast.success('Audience enregistrée');
          onClose();
        },
        onError: (err) => {
          toast.error(err instanceof Error ? err.message : 'Impossible d’enregistrer');
        },
      },
    );
  }

  const dossierLine = dossier.data ? `${dossier.data.number} · ${dossier.data.title}` : undefined;

  return (
    <Shell title="Nouvelle audience" onClose={onClose} bare>
      <div className="overflow-y-auto">
        {repick || !dossierId ? (
          <div className="border-b border-[#e6e2d8] bg-white">
            {repick && dossierId ? (
              <button
                type="button"
                onClick={() => setRepick(false)}
                className="px-6 pt-3 text-[13px] font-bold text-cime-primary"
              >
                Revenir à la fiche
              </button>
            ) : null}
            <DossierPickStep
              key={repick ? 'repick' : 'pick'}
              selectedId={dossierId}
              onSelect={(id) => {
                setDossierId(id);
                setRepick(false);
              }}
              startOpen={repick || !dossierId}
            />
          </div>
        ) : null}
        <HearingFields
          draft={draft}
          onChange={setDraft}
          disabled={!dossierId || create.isPending}
          collaborators={options.collaborators}
          parties={options.parties}
          context={dossierLine}
          onChangeContext={dossierId ? () => setRepick(true) : undefined}
          title={(
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-cime-gold-text">
              Nouvelle audience
            </p>
          )}
        />
      </div>
      <div className="flex justify-end gap-2 border-t border-[#e6e2d8] bg-white px-[22px] py-3.5">
        <Button
          type="button"
          variant="ghost"
          onClick={onClose}
          className="border border-[#e6e2d8] bg-white font-bold text-cime-text-primary hover:bg-[#f6f4ee]"
        >
          Annuler
        </Button>
        <Button
          type="button"
          onClick={save}
          disabled={!dossierId || !committed.ok || create.isPending}
          isLoading={create.isPending}
          className="font-bold"
        >
          Enregistrer l’audience
        </Button>
      </div>
    </Shell>
  );
}

export function QuickCreateTaskDialog({
  contextDossierId,
  onClose,
}: {
  contextDossierId: string | null;
  onClose: () => void;
}) {
  const [dossierId, setDossierId] = useState<string | null>(contextDossierId);
  const { data: quickCapture = false } = useTaskQuickCapture();

  if (!quickCapture) {
    return (
      <TaskCreateModal open onClose={onClose} dossierId={contextDossierId} />
    );
  }

  return (
    <Shell title="Nouvelle tâche" onClose={onClose} wide>
      <DossierPickStep
        selectedId={dossierId}
        onSelect={setDossierId}
        required={false}
        onClear={() => setDossierId(null)}
      />
      <div className="p-5">
        <TaskComposer
          dossierId={dossierId}
          placeholder="Nouvelle tâche…"
          onCreated={() => {
            toast.success('Tâche créée');
            onClose();
          }}
        />
      </div>
    </Shell>
  );
}

export function QuickCreateTimeEntryDialog({
  contextDossierId,
  onClose,
}: {
  contextDossierId: string | null;
  onClose: () => void;
}) {
  const [dossierId, setDossierId] = useState<string | null>(contextDossierId);

  if (dossierId) {
    return (
      <TimeEntryModal
        open
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        dossierId={dossierId}
      />
    );
  }

  return (
    <Shell title="Nouvelle prestation" onClose={onClose}>
      <DossierPickStep selectedId={dossierId} onSelect={setDossierId} />
    </Shell>
  );
}
